import { createFileRoute, notFound } from "@tanstack/react-router"

import { authorSchema, getBlogAuthors, parseAuthorNames } from "@/lib/blog-authors"
import { blogPosts, getBlogPost } from "@/lib/blog-posts"
import { RSS_FEED_LINK } from "@/lib/rss-meta"
import { seoMeta } from "@/lib/seo"

import { BlogArticlePage } from "./-components/BlogArticlePage"

export const Route = createFileRoute("/_content/blog/$slug")({
  loader: async ({ params }) => {
    const { getBlogPost } = await import("@/lib/blog-posts")
    const post = getBlogPost(params.slug)
    if (!post) throw notFound()
    // Return only serializable data — component looked up in render
    return {
      slug: post.slug,
      date: post.date,
      dateFormatted: post.dateFormatted,
      frontmatter: post.frontmatter,
    }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const fm = loaderData.frontmatter
    const title = fm["title"] as string
    const description = fm["description"] as string
    const author = fm["author"] as string | undefined
    const authors = getBlogAuthors(author)
    const image =
      (fm["image"] as string | undefined) || `https://entire.com/og/blog/${loaderData.slug}.png`
    const url = `https://entire.com/blog/${loaderData.slug}`
    return {
      ...seoMeta({
        title,
        description,
        url,
        image,
        imageWidth: typeof fm["imageWidth"] === "number" ? fm["imageWidth"] : undefined,
        imageHeight: typeof fm["imageHeight"] === "number" ? fm["imageHeight"] : undefined,
        type: "article",
        extra: [
          { property: "article:published_time", content: loaderData.date },
          { property: "article:section", content: fm["category"] as string },
        ],
        extraLinks: [RSS_FEED_LINK],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            image,
            datePublished: loaderData.date,
            ...(authors.length > 0 && {
              author: parseAuthorNames(author).map((name) => {
                const profile = getBlogAuthors(name)[0]
                return profile ? authorSchema(profile) : { "@type": "Person", name }
              }),
            }),
            publisher: {
              "@type": "Organization",
              name: "Entire",
              logo: { "@type": "ImageObject", url: "https://entire.com/favicon.svg" },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://entire.com" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://entire.com/blog" },
              { "@type": "ListItem", position: 3, name: title, item: url },
            ],
          }),
        },
      ],
    }
  },
  component: BlogPostRoute,
})

function BlogPostRoute() {
  const data = Route.useLoaderData()
  const post = getBlogPost(data.slug)!

  return <BlogArticlePage post={post} posts={blogPosts} />
}
